/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { VehiclePartCategoryTreeItem } from '@1po/1po-bff-fe-spec/generated/catalog/category/model/VehiclePartCategoryTreeItem';
import { ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';
import { DotIcon, OperationsPersonIcon } from 'assets/icons';
import { getEasyBasketEnabled } from 'domains/appContext/AppContext.store';
import { getLastSearchedVehicleKey, getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { ActiveTabParam, PARAM_OPERATIONS, PreferredTabParam } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { BlackButton, Flex, Icon, sentenceCase } from 'UI';
import { Text } from 'UI/Text';
import { SL1SideMenu, SL2GroupHeader, SL3Item, SL3ItemsGrid, SListViewWrapper } from './CarPartGroupsListView.styled';
import { ROUTINE_VEHICLE_MAINTENANCE_GROUP_NODE_ID, useRvmL2Sections } from '../CarPartGroupsSection.utils';

interface CarPartGroupsListViewProps {
  rvmNode: VehiclePartCategoryTreeItem | undefined;
  lvl1Nodes: VehiclePartCategoryTreeItem[];
}

export const CarPartGroupsListView = ({ rvmNode, lvl1Nodes }: CarPartGroupsListViewProps) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { carPartGroupId } = useParams<{ carPartGroupId?: string }>();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const isEasyBasketEnabled = useSelector(getEasyBasketEnabled);

  const allItems = rvmNode ? [rvmNode, ...lvl1Nodes] : lvl1Nodes;

  const resolveActiveLvl1 = () => {
    if (carPartGroupId && allItems.some((item) => item.nodeId === carPartGroupId)) {
      return carPartGroupId;
    }
    return allItems[0]?.nodeId ?? '';
  };

  const [activeLvl1NodeId, setActiveLvl1NodeId] = useState<string>(resolveActiveLvl1);

  // Sync with URL param when switching views or navigating
  useEffect(() => {
    setActiveLvl1NodeId(resolveActiveLvl1());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carPartGroupId, lvl1Nodes.length]);

  const isRvmActive = activeLvl1NodeId === ROUTINE_VEHICLE_MAINTENANCE_GROUP_NODE_ID;

  const rvmL2Sections = useRvmL2Sections(rvmNode, lvl1Nodes);

  const activeLvl1 = lvl1Nodes.find((item) => item.nodeId === activeLvl1NodeId);
  const lvl2Items: VehiclePartCategoryTreeItem[] = activeLvl1?.items ?? [];

  const navigateTo = (path: string) => {
    if (vehicleDetail) {
      history.push({
        pathname: `${getCatalogSourceUrl(vehicleDetail.catalogSource)}/${vehicleKey}${ROUTER_CATALOG_VEHICLE}/${path}`,
        search: location.search,
      });
    }
  };

  const navigateToEasyBasket = (l2Path: string, l3NodeId?: string) => {
    if (vehicleDetail) {
      const urlParams = new URLSearchParams(location.search);
      urlParams.set(ActiveTabParam, PARAM_OPERATIONS);
      urlParams.set(PreferredTabParam, PARAM_OPERATIONS);
      const l3Segment = l3NodeId ? `/${l3NodeId}` : '';
      history.push({
        pathname: `${getCatalogSourceUrl(
          vehicleDetail.catalogSource,
        )}/${vehicleKey}${ROUTER_CATALOG_VEHICLE}/${l2Path}${l3Segment}`,
        search: urlParams.toString(),
      });
      scrollTo(0, 0);
    }
  };

  const selectLvl1 = (nodeId: string) => {
    setActiveLvl1NodeId(nodeId);
    if (vehicleDetail) {
      history.replace({
        pathname: `${getCatalogSourceUrl(
          vehicleDetail.catalogSource,
        )}/${vehicleKey}${ROUTER_CATALOG_VEHICLE}/${nodeId}`,
        search: location.search,
      });
    }
  };

  const menuItems = allItems.map((item) => ({
    key: item.nodeId,
    label: <Text type="base">{sentenceCase(item.label)}</Text>,
  }));

  return (
    <Flex direction={'column'} align={'center'}>
      <SListViewWrapper align={'stretch'}>
        <SL1SideMenu items={menuItems} onSelect={({ key }) => selectLvl1(key)} selectedKeys={[activeLvl1NodeId]} />

        {/* Main content */}
        <Flex direction={'column'} padding={'0 0 0 24px'} gap={15} alignSelf={'flex-start'}>
          {isRvmActive
            ? // RVM: render resolved L2 sections (Front / Rear) with L3 bullet items
              rvmL2Sections.map((l2, index) => (
                <Flex key={l2.nodeId} direction={'column'} gap={10}>
                  <SL2GroupHeader align={'center'} justify={'space-between'} padding={'5px 10px'} gap={10}>
                    <Text
                      type="heading4"
                      onClick={() => navigateTo(`${ROUTINE_VEHICLE_MAINTENANCE_GROUP_NODE_ID}/${l2.nodeId}`)}
                      hoverUnderLine
                      cursor={'pointer'}
                    >{`${index + 1}. ${sentenceCase(l2.label)}`}</Text>
                    {!!l2.operationIds?.length && isEasyBasketEnabled && (
                      <BlackButton
                        shape={'round'}
                        size={'middle'}
                        icon={<Icon IconComponent={OperationsPersonIcon} color={'white'} size={24} />}
                        onClick={() =>
                          navigateToEasyBasket(
                            `${ROUTINE_VEHICLE_MAINTENANCE_GROUP_NODE_ID}/${l2.nodeId}`,
                            l2.l3Items.find((l3) => l3.operationIds.length > 0)?.nodeId,
                          )
                        }
                      >
                        {t('catalog.operations.title', 'Operations')}
                      </BlackButton>
                    )}
                  </SL2GroupHeader>
                  {l2.l3Items.length > 0 && (
                    <SL3ItemsGrid>
                      {l2.l3Items.map((l3) => (
                        <SL3Item key={l3.nodeId} gap={10} direction={'row'}>
                          <Icon IconComponent={DotIcon} size={7} noPointer />
                          <Text type="base" hoverUnderLine cursor={'pointer'} onClick={() => navigateTo(l3.path)}>
                            {sentenceCase(l3.label)}
                          </Text>
                        </SL3Item>
                      ))}
                    </SL3ItemsGrid>
                  )}
                </Flex>
              ))
            : // Regular L1: render L2 sections with L3 bullet items
              lvl2Items.map((lvl2, index) => (
                <Flex key={lvl2.nodeId} direction={'column'} gap={10}>
                  <SL2GroupHeader align={'center'} justify={'space-between'} padding={'5px 10px'} gap={10}>
                    <Text
                      type="heading4"
                      onClick={() => navigateTo(`${activeLvl1NodeId}/${lvl2.nodeId}`)}
                      hoverUnderLine
                      cursor={'pointer'}
                    >
                      {`${index + 1}. ${sentenceCase(lvl2.label)}`}
                    </Text>
                    {!!lvl2.operationIds?.length && isEasyBasketEnabled && (
                      <BlackButton
                        shape={'round'}
                        size={'middle'}
                        icon={<Icon IconComponent={OperationsPersonIcon} color={'white'} size={24} />}
                        onClick={() =>
                          navigateToEasyBasket(
                            `${activeLvl1NodeId}/${lvl2.nodeId}`,
                            lvl2.items.find((l3) => l3.operationIds.length > 0)?.nodeId,
                          )
                        }
                      >
                        {t('catalog.operations.title', 'Operations')}
                      </BlackButton>
                    )}
                  </SL2GroupHeader>

                  {lvl2.items && lvl2.items.length > 0 && (
                    <SL3ItemsGrid>
                      {lvl2.items.map((lvl3) => (
                        <SL3Item key={lvl3.nodeId} gap={10} direction={'row'}>
                          <Icon IconComponent={DotIcon} size={7} noPointer />
                          <Text
                            type="base"
                            hoverUnderLine
                            cursor={'pointer'}
                            onClick={() => navigateTo(`${activeLvl1NodeId}/${lvl2.nodeId}/${lvl3.nodeId}`)}
                          >
                            {sentenceCase(lvl3.label)}
                          </Text>
                        </SL3Item>
                      ))}
                    </SL3ItemsGrid>
                  )}
                </Flex>
              ))}
        </Flex>
      </SListViewWrapper>
    </Flex>
  );
};
